import { useEffect, useMemo, useState } from 'react';
import useGetWeightedScores from './useGetWeightedScores';
import { useIsFeatureEnabled, useLocalStorage } from '@innovyze/stylovyze';
import { useRiskDetailsSelectors } from './useRiskDetailsSelectors';
import { useResultsGridFlags } from './useResultsGridFlags';
import { useKeyedLocalStorage } from './useKeyedLocalStorage';

type VisibilityModel = Record<string, boolean>;

export default function useGetRiskDetailsColumnVisibility(configId: string) {
	const weightedScores = useGetWeightedScores();
	const { tenantId, userId } = useRiskDetailsSelectors();

	const [cofFirstRender, setCofFirstRender] = useState(true);

	const isIndividualScoresEnabled = useIsFeatureEnabled(
		'info-360-risk-datagrid-individual-scores',
	);

	const { isRedesign } = useResultsGridFlags();

	const [legacyModel, setLegacyModel] = useLocalStorage<
		VisibilityModel | undefined
	>(
		`${userId}.${tenantId}.riskTable.columnVisibilityModel.${configId}`,
		undefined,
	);

	// tenantId is typed unknown by the selector; only a string may build a key.
	const tenant = typeof tenantId === 'string' ? tenantId : '';

	// Flag on: re-reads when configId, userId or tenantId change after mount.
	const [keyedSaved, setKeyedSaved] = useKeyedLocalStorage<VisibilityModel>(
		userId && tenant && configId
			? `${userId}.${tenant}.riskTable.columnVisibilityModel.${configId}`
			: null,
	);

	const weightedCofColumnNames = weightedScores.cofScores
		?.map(cof => (cof.field.includes('Raw') ? cof.field : null))
		.filter(Boolean);
	const weightedLofColumnNames = weightedScores.lofScores
		.map(lof => lof.field)
		.filter(Boolean);

	useEffect(() => {
		if (
			!isRedesign &&
			isIndividualScoresEnabled &&
			!legacyModel &&
			cofFirstRender &&
			(weightedCofColumnNames.length > 0 ||
				weightedLofColumnNames.length > 0)
		) {
			const cof = weightedCofColumnNames
				.filter(el => el?.includes('Raw'))
				.map(el => ({ [el ?? '']: false }));

			const lof = weightedLofColumnNames
				.filter(el => el?.includes('Raw'))
				.map(el => ({ [el.toString()]: false }));

			const joined = [...cof, ...lof].reduce(
				(acc, val) => ({ ...acc, ...val }),
				{},
			) as Record<string, boolean>;

			setLegacyModel(cvm => ({ ...cvm, ...joined }));
			setCofFirstRender(false);
		}
	}, [weightedScores.cofScores, weightedScores.lofScores]);

	// Flag on: merge driver-hidden defaults under whatever is saved, so every
	// driver column (contribution LOF_/COF_ and raw LOFRaw_/COFRaw_) starts
	// hidden, including new ones for users with an older saved model. An
	// explicit choice (true or false) always wins. Derived, never written.
	const driverFields = [
		...weightedScores.lofScores,
		...(weightedScores.cofScores ?? []),
	]
		.map(c => c.field)
		.filter(Boolean);
	// Joined only as a stable memo dependency; the fields array is the source.
	const driverHiddenKey = driverFields.join('|');

	// Memoised so the controlled grid prop keeps a stable identity between renders.
	const mergedModel = useMemo<VisibilityModel>(
		() => ({
			...driverFields.reduce<VisibilityModel>((acc, f) => {
				acc[f] = false;
				return acc;
			}, {}),
			...keyedSaved,
		}),
		[driverHiddenKey, keyedSaved],
	);

	// MUI's "Show all" deletes keys instead of setting them true, and the merge
	// above would re-hide driver columns missing from the saved model. MUI
	// passes the full model derived from the merged one, so a missing key
	// means shown.
	const setRedesignModel = (model: VisibilityModel) => {
		const next = { ...model };
		driverFields.forEach(f => {
			if (!(f in next)) next[f] = true;
		});
		setKeyedSaved(next);
	};

	return {
		columnVisibilityModel: isRedesign ? mergedModel : legacyModel,
		setColumnVisibilityModel: (isRedesign
			? setRedesignModel
			: setLegacyModel) as (model: VisibilityModel) => void,
	};
}
